import { STREAM_PRIVACY_MASK } from '../config/streamPrivacy';

function parsePercent(value) {
    if (typeof value === 'number') return value / 100;
    return parseFloat(String(value).replace('%', '')) / 100;
}

export function privacyCornerRadius(width) {
    return Math.round(Math.min(32, Math.max(14, width * 0.028)));
}

function roundRectRightOnly(ctx, x, y, w, h, r) {
    const rad = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w - rad, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rad);
    ctx.lineTo(x + w, y + h - rad);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
    ctx.lineTo(x, y + h);
    ctx.closePath();
}

/** Ten sam kształt co mask-image w .stream-privacy-blur (CSS). */
export function buildFeatherMask(width, height, cornerR) {
    const mask = document.createElement('canvas');
    mask.width = width;
    mask.height = height;
    const mctx = mask.getContext('2d');
    if (!mctx) return mask;

    mctx.fillStyle = '#fff';
    roundRectRightOnly(mctx, 0, 0, width, height, cornerR);
    mctx.fill();

    mctx.globalCompositeOperation = 'destination-in';
    const gradX = mctx.createLinearGradient(0, 0, width, 0);
    gradX.addColorStop(0, 'rgba(255,255,255,1)');
    gradX.addColorStop(0.36, 'rgba(255,255,255,1)');
    gradX.addColorStop(0.5, 'rgba(255,255,255,0.82)');
    gradX.addColorStop(0.62, 'rgba(255,255,255,0.45)');
    gradX.addColorStop(0.74, 'rgba(255,255,255,0.12)');
    gradX.addColorStop(0.86, 'rgba(255,255,255,0)');
    mctx.fillStyle = gradX;
    mctx.fillRect(0, 0, width, height);

    const gradY = mctx.createLinearGradient(0, 0, 0, height);
    gradY.addColorStop(0, 'rgba(255,255,255,1)');
    gradY.addColorStop(0.28, 'rgba(255,255,255,1)');
    gradY.addColorStop(0.44, 'rgba(255,255,255,0.85)');
    gradY.addColorStop(0.58, 'rgba(255,255,255,0.5)');
    gradY.addColorStop(0.72, 'rgba(255,255,255,0.15)');
    gradY.addColorStop(0.86, 'rgba(255,255,255,0)');
    mctx.fillStyle = gradY;
    mctx.fillRect(0, 0, width, height);

    return mask;
}

function intersectViewportRects(a, b) {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) return null;
    return { left, top, right, bottom, width: right - left, height: bottom - top };
}

function resolveMaskElement(videoEl) {
    return videoEl?.closest?.('.player-stage')?.querySelector?.('.stream-privacy-blur') ?? null;
}

/**
 * Mapuje widoczny prostokąt (viewport) wspólny dla maski i wideo na piksele klatki (object-fit: contain).
 */
function viewportRectToFramePixels(viewRect, videoRect, vw, vh) {
    const ew = videoRect.width;
    const eh = videoRect.height;
    const scale = Math.min(ew / vw, eh / vh);
    const cw = vw * scale;
    const ch = vh * scale;
    const ox = (ew - cw) / 2;
    const oy = (eh - ch) / 2;

    const ex0 = viewRect.left - videoRect.left;
    const ey0 = viewRect.top - videoRect.top;
    const ex1 = viewRect.right - videoRect.left;
    const ey1 = viewRect.bottom - videoRect.top;

    const cx0 = Math.max(ex0, ox);
    const cy0 = Math.max(ey0, oy);
    const cx1 = Math.min(ex1, ox + cw);
    const cy1 = Math.min(ey1, oy + ch);

    if (cx1 <= cx0 || cy1 <= cy0) return null;

    const inv = 1 / scale;
    let x = Math.floor((cx0 - ox) * inv);
    let y = Math.floor((cy0 - oy) * inv);
    let x2 = Math.ceil((cx1 - ox) * inv);
    let y2 = Math.ceil((cy1 - oy) * inv);
    x = Math.max(0, Math.min(vw - 1, x));
    y = Math.max(0, Math.min(vh - 1, y));
    x2 = Math.max(x + 1, Math.min(vw, x2));
    y2 = Math.max(y + 1, Math.min(vh, y2));
    return { x, y, bw: x2 - x, bh: y2 - y };
}

/**
 * Geometria maski z DOM (to samo co widać na ekranie), przycięta do stage (overflow),
 * potem mapowanie na klatkę wideo.
 */
function computePrivacyMaskRectFromDom(maskEl, videoEl, vw, vh) {
    const stageEl = videoEl?.closest?.('.player-stage');
    if (!stageEl || !maskEl || !videoEl || vw <= 0 || vh <= 0) return null;

    const maskRect = maskEl.getBoundingClientRect();
    const videoRect = videoEl.getBoundingClientRect();
    const stageRect = stageEl.getBoundingClientRect();

    if (
        maskRect.width <= 0 ||
        maskRect.height <= 0 ||
        videoRect.width <= 0 ||
        videoRect.height <= 0 ||
        stageRect.width <= 0 ||
        stageRect.height <= 0
    ) {
        return null;
    }

    const clippedMask = intersectViewportRects(maskRect, stageRect);
    if (!clippedMask) return null;

    const onVideo = intersectViewportRects(clippedMask, videoRect);
    if (!onVideo) return null;

    return viewportRectToFramePixels(onVideo, videoRect, vw, vh);
}

/**
 * Zapasowa ścieżka: procenty względem `.player-stage` (gdy brak maski w DOM).
 */
function computePrivacyMaskRectFromStagePercent(blur, frameWidth, frameHeight, videoEl) {
    const vw = frameWidth;
    const vh = frameHeight;
    const stageEl = videoEl?.closest?.('.player-stage');

    if (stageEl && videoEl && vw > 0 && vh > 0) {
        const stageRect = stageEl.getBoundingClientRect();
        const videoRect = videoEl.getBoundingClientRect();
        if (stageRect.width > 0 && stageRect.height > 0 && videoRect.width > 0 && videoRect.height > 0) {
            const sw = stageRect.width;
            const sh = stageRect.height;
            const maskX0 = parsePercent(blur.left) * sw;
            const maskY0 = parsePercent(blur.top) * sh;
            const maskX1 = maskX0 + parsePercent(blur.width) * sw;
            const maskY1 = Math.min(maskY0 + parsePercent(blur.height) * sh, sh);

            const stageView = {
                left: stageRect.left,
                top: stageRect.top,
                right: stageRect.right,
                bottom: stageRect.bottom,
            };
            const maskView = {
                left: stageRect.left + maskX0,
                top: stageRect.top + maskY0,
                right: stageRect.left + maskX1,
                bottom: stageRect.top + maskY1,
            };
            const clipped = intersectViewportRects(maskView, stageView);
            if (clipped) {
                const ix0 = clipped.left - stageRect.left;
                const iy0 = clipped.top - stageRect.top;
                const ix1 = clipped.right - stageRect.left;
                const iy1 = clipped.bottom - stageRect.top;

                const videoView = {
                    left: videoRect.left,
                    top: videoRect.top,
                    right: videoRect.right,
                    bottom: videoRect.bottom,
                };
                const maskStageView = {
                    left: stageRect.left + ix0,
                    top: stageRect.top + iy0,
                    right: stageRect.left + ix1,
                    bottom: stageRect.top + iy1,
                };
                const onVideo = intersectViewportRects(maskStageView, videoView);
                if (onVideo) {
                    const mapped = viewportRectToFramePixels(onVideo, videoRect, vw, vh);
                    if (mapped && mapped.bw > 0 && mapped.bh > 0) return mapped;
                }
            }
        }
    }

    const x = Math.max(0, Math.round(parsePercent(blur.left) * vw));
    const y = Math.max(0, Math.round(parsePercent(blur.top) * vh));
    const bw = Math.min(vw - x, Math.round(parsePercent(blur.width) * vw));
    const bh = Math.min(vh - y, Math.round(parsePercent(blur.height) * vh));
    return { x, y, bw, bh };
}

export function computePrivacyMaskRect(blur, frameWidth, frameHeight, videoEl) {
    const maskEl = resolveMaskElement(videoEl);
    if (maskEl) {
        const fromDom = computePrivacyMaskRectFromDom(maskEl, videoEl, frameWidth, frameHeight);
        if (fromDom && fromDom.bw > 0 && fromDom.bh > 0) return fromDom;
    }
    return computePrivacyMaskRectFromStagePercent(blur, frameWidth, frameHeight, videoEl);
}

/** Rozmyta łatka maski (w pikselach klatki) — do zrzutów ekranu i nakładki na żywo. */
export function createPrivacyMaskPatch(
    sourceCanvas,
    frameWidth,
    frameHeight,
    config = STREAM_PRIVACY_MASK,
    { videoEl, extendBottom } = {}
) {
    if (!config?.enabled || !sourceCanvas) return null;

    let { x, y, bw, bh } = computePrivacyMaskRect(config.blur, frameWidth, frameHeight, videoEl);

    const extraBottom = extendBottom ?? config.extendBottom;
    if (extraBottom) {
        const extra = Math.round(parsePercent(extraBottom) * frameHeight);
        bh = Math.min(frameHeight - y, bh + extra);
    }

    if (bw <= 0 || bh <= 0) return null;

    const bleed = Math.round(Math.min(frameWidth, frameHeight) * 0.04);
    const blurPx = Math.round(22 * (frameWidth / 1280));
    const cornerR = privacyCornerRadius(frameWidth);

    const scratch = document.createElement('canvas');
    scratch.width = bw + bleed * 2;
    scratch.height = bh + bleed * 2;
    const sctx = scratch.getContext('2d');
    if (!sctx) return null;

    sctx.filter = `blur(${blurPx}px)`;
    sctx.drawImage(
        sourceCanvas,
        x - bleed,
        y - bleed,
        bw + bleed * 2,
        bh + bleed * 2,
        0,
        0,
        scratch.width,
        scratch.height
    );
    sctx.filter = 'none';
    sctx.fillStyle = 'rgba(12, 18, 28, 0.28)';
    sctx.fillRect(0, 0, scratch.width, scratch.height);

    const patch = document.createElement('canvas');
    patch.width = bw;
    patch.height = bh;
    const pctx = patch.getContext('2d');
    if (!pctx) return null;

    pctx.drawImage(scratch, bleed, bleed, bw, bh, 0, 0, bw, bh);

    const feather = buildFeatherMask(bw, bh, cornerR);
    pctx.globalCompositeOperation = 'destination-in';
    pctx.drawImage(feather, 0, 0);

    return { patch, x, y, bw, bh };
}

/** Rozmycie lewej strefy na canvasie — zsynchronizowane z widoczną maską `.stream-privacy-blur`. */
export function drawStreamPrivacyMask(
    ctx,
    sourceCanvas,
    frameWidth,
    frameHeight,
    config = STREAM_PRIVACY_MASK,
    options = {}
) {
    if (!ctx || !sourceCanvas) return;
    const result = createPrivacyMaskPatch(sourceCanvas, frameWidth, frameHeight, config, options);
    if (!result) return;
    ctx.drawImage(result.patch, result.x, result.y);
}
